// Khung chung cho Đăng nhập / Đăng ký theo bản thiết kế mới: đầu trang (logo, ngôn ngữ, giao diện),
// thẻ 2 cột (giới thiệu bên trái, biểu mẫu bên phải); dưới 900px chỉ còn thẻ biểu mẫu.
import { ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import { useField } from "formik";
import clsx from "clsx";
import { usePreferences, useT } from "@app/contexts/preferences.context";
import { MAX_LEN } from "@app/constants";
import {
  IcExternalLink,
  IcEye,
  IcEyeOff,
  IcMonitor,
  IcMoon,
  IcServer,
  IcSun,
  IcZap,
} from "../icons";
import { iconBtn } from "../ui";

const GUIDE_URL = "https://youtu.be/c9W5mGHIDX0";

export function AuthLayout({ children, wide }: { children: ReactNode; wide?: boolean }) {
  const t = useT();
  const { lang, setLang, isDark, setTheme } = usePreferences();

  const points = [
    [IcServer, t("Máy chủ, VPS, PC cấu hình cao có GPU")],
    [IcMonitor, t("Điều khiển từ xa qua Remote Desktop, ở đâu cũng dùng được")],
    [IcZap, t("Thuê tự động 24/7, chỉ vài cú bấm")],
  ] as const;

  return (
    <div className="flex min-h-screen flex-col bg-bg text-fg">
      <header className="mx-auto flex h-16 w-full max-w-[1290px] flex-none items-center gap-3 px-6 max-[900px]:px-4 max-[420px]:gap-2">
        <Link
          className="flex items-center gap-2 px-2 pb-1.5 pt-1 text-xl font-bold tracking-[-.01em] max-[420px]:px-0"
          to="/home"
          aria-label={t("PC Render trang chủ")}
        >
          <span className="rounded-[7px] bg-brand px-[7px] py-px text-[17px] text-brand-ink">PC</span>Render
        </Link>
        <span className="flex-1" />
        <button
          type="button"
          className={clsx(iconBtn, "!w-auto px-2.5 text-[13px] font-semibold")}
          aria-label={t("Ngôn ngữ")}
          title={t("Ngôn ngữ")}
          onClick={() => setLang(lang === "vi" ? "en" : "vi")}
        >
          {lang === "vi" ? "VI" : "EN"}
        </button>
        <button
          type="button"
          className={clsx(iconBtn, "text-[18px]")}
          aria-label={t("Giao diện")}
          title={t("Giao diện")}
          onClick={() => setTheme(isDark ? "light" : "dark")}
        >
          {isDark ? <IcMoon /> : <IcSun />}
        </button>
      </header>

      {/* dưới 900px chừa 88px cuối trang để nút hỗ trợ nổi không che dòng cuối của thẻ */}
      <main className="flex flex-1 flex-col items-center justify-center px-6 pb-12 pt-4 max-[900px]:px-4 max-[900px]:pb-[88px] max-[480px]:pt-1">
        <div
          className={clsx(
            "grid w-full overflow-hidden rounded-2xl border border-line bg-surface shadow-card",
            wide
              ? "max-w-[1120px] grid-cols-[minmax(0,1fr)_minmax(0,600px)] max-[1240px]:grid-cols-[minmax(0,1fr)_minmax(0,540px)]"
              : "max-w-[960px] grid-cols-[minmax(0,1fr)_440px]",
            "max-[900px]:max-w-[520px] max-[900px]:grid-cols-1"
          )}
        >
          <aside className="auth-hero flex flex-col gap-5 p-9 text-white max-[1100px]:p-7 max-[900px]:hidden">
            <span className="w-fit rounded-full bg-white/10 px-3 py-1 text-[12px] font-semibold italic tracking-wide">
              &quot;Happy rendering&quot;
            </span>
            <div>
              <h2 className="text-[26px] font-bold leading-tight tracking-[-.015em]">{t("Chào mừng bạn tới PC Render")}</h2>
              <p className="mb-0 mt-2.5 text-[14px] leading-relaxed text-white/75">
                {t(
                  "Dịch vụ cho thuê máy chủ, server, VPS và PC cấu hình cao có GPU. Chuyển việc nặng sang máy của chúng tôi để máy bạn nhẹ hơn."
                )}
              </p>
            </div>
            <ul className="m-0 flex list-none flex-col gap-3 p-0">
              {points.map(([Icon, text]) => (
                <li key={text} className="flex items-start gap-3 text-[13.5px] text-white/90">
                  <span className="grid h-8 w-8 flex-none place-items-center rounded-[9px] bg-white/10">
                    <Icon className="h-[17px] w-[17px]" />
                  </span>
                  <span className="pt-1.5">{text}</span>
                </li>
              ))}
            </ul>
            <a
              className="mt-auto inline-flex w-fit items-center gap-2 rounded-[9px] border border-white/25 px-3.5 py-2 text-[13px] font-semibold text-white hover:bg-white/10"
              href={GUIDE_URL}
              target="_blank"
              rel="noreferrer"
            >
              <IcExternalLink className="h-4 w-4" />
              {t("Xem video hướng dẫn sử dụng")}
            </a>
          </aside>

          <section className="flex min-w-0 flex-col p-9 max-[1100px]:p-7 max-[480px]:px-5 max-[480px]:py-6 max-[400px]:px-4">
            {children}
          </section>
        </div>
        {/* cột giới thiệu ẩn dưới 900px: giữ link video hướng dẫn như bản cũ */}
        <a
          className="mt-4 hidden items-center gap-1.5 text-[13px] font-semibold text-muted hover:text-brand max-[900px]:inline-flex"
          href={GUIDE_URL}
          target="_blank"
          rel="noreferrer"
        >
          <IcExternalLink className="h-4 w-4" />
          {t("Xem video hướng dẫn sử dụng")}
        </a>
      </main>
    </div>
  );
}

/** Tiêu đề của thẻ biểu mẫu */
export function AuthHead({ title, sub }: { title: string; sub?: ReactNode }) {
  return (
    <div className="mb-6 max-[480px]:mb-5">
      <h1 className="m-0 text-[24px] font-bold tracking-[-.015em] max-[480px]:text-[22px]">{title}</h1>
      {sub && <p className="mb-0 mt-1 text-[13.5px] text-muted">{sub}</p>}
    </div>
  );
}

interface AuthFieldProps {
  name: string;
  label: string;
  icon: ReactNode;
  type?: "text" | "password" | "email" | "tel";
  placeholder?: string;
  autoComplete?: string;
  readOnly?: boolean;
  optional?: boolean;
  autoFocus?: boolean;
  /** Mặc định MAX_LEN.TEXT */
  maxLength?: number;
}

/** Ô nhập của Formik: nhãn trên, icon trái, lỗi ngay dưới ô; ô mật khẩu có nút hiện/ẩn */
export function AuthField({ name, label, icon, type = "text", optional, maxLength = MAX_LEN.TEXT, ...rest }: AuthFieldProps) {
  const t = useT();
  const [field, meta] = useField(name);
  const [show, setShow] = useState(false);
  const bad = meta.touched && !!meta.error;
  const isPw = type === "password";

  return (
    <label className="flex min-w-0 flex-col gap-1.5">
      <span className="text-[12.5px] font-semibold text-muted">
        {label}
        {optional && <small className="ml-1 font-normal text-faint">({t("không bắt buộc")})</small>}
      </span>
      <span className="relative block">
        <span className="pointer-events-none absolute left-3 top-1/2 grid -translate-y-1/2 text-faint [&>svg]:h-[17px] [&>svg]:w-[17px]">
          {icon}
        </span>
        <input
          {...field}
          {...rest}
          type={isPw && show ? "text" : type}
          maxLength={maxLength}
          aria-invalid={bad}
          className={clsx(
            "h-10 w-full rounded-[10px] border bg-surface pl-10 text-fg outline-none placeholder:text-faint focus:border-brand focus:shadow-[0_0_0_3px_var(--brand-soft)]",
            isPw ? "pr-10" : "pr-3",
            rest.readOnly && "bg-surface-2 text-muted",
            bad ? "border-bad" : "border-line"
          )}
        />
        {isPw && (
          <button
            type="button"
            className="absolute right-1.5 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-md text-faint hover:bg-surface-2 hover:text-fg"
            aria-label={show ? t("Ẩn mật khẩu") : t("Hiện mật khẩu")}
            onClick={() => setShow(!show)}
          >
            {show ? <IcEyeOff className="h-4 w-4" /> : <IcEye className="h-4 w-4" />}
          </button>
        )}
      </span>
      {bad && <span className="text-[12px] font-medium text-bad">{t(meta.error as string)}</span>}
    </label>
  );
}
